import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, ButtonGroup, useTheme } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { Plot } from './Plot';
import {
  TraceLense,
  TraceStrikeBarType,
  GammaHeatmapDatum,
  PriceCandleWithDateTime,
  PriceLineKey,
  ProductType,
  RawSpotgammaLevels,
  ZoomData,
} from '../../types';
import { ExpandableContentWrapper, Loader } from '../../components';
import { Center } from '../../components/shared/Center';
import {
  getGreekValueAtLastPrice,
  getHiroRange,
  getYRange,
  parseContourArray,
  shouldShowAxisLabels,
  strikeBarParquetUrlForType,
  getContourData,
  getContourColorOptions,
} from '../../util/oi';
import { TraceControls } from './TraceControls';
import { useSetSym } from '../../hooks';
import { Dayjs } from 'dayjs';
import {
  dayjs,
  formatAsCompactNumber,
  getDateFormatted,
  getPriceLines,
  getTzOffsetMs,
  hexToRGBA,
  predicateSearch,
  sigHighLow,
  expectedStartEndForSymbol,
  safeMerge,
  splitCandle,
} from '../../util';
import { TRACE_BAR_TITLES, TraceShowChartType } from '../../config/oi';
import { TraceTimeSlider } from './TraceTimeSlider';
import Plotly, { Image } from 'plotly.js';
import { useStrikeBars } from '../../hooks/trace/useStrikeBars';
import { useTooltip } from '../../hooks/trace/useTooltip';
import { useStats } from '../../hooks/trace/useStats';
import { useTraceParams } from '../../hooks/trace/useTraceParams';
import useUserDetails from '../../hooks/user/useUserDetails';
import {
  oiIntradayParquetKeys,
  oiIntradayPriceBoundsState,
  timezoneState,
  screenWidthWithoutSidebarState,
  screenHeightState,
  isMobileState,
  oiStrikeBarTypeState,
  oiFullscreenState,
  todaysOpenArrState,
  oiShowKeyLevelsState,
  oiShowGexZeroDteState,
  oiShowColorScaleState,
  oiShowContourLinesState,
  oiPriceCandleDurationState,
  oiIntradayInvertedState,
  oiScaleRangeState,
  oiHiroSymsState,
} from '../../states';
import { decode } from '@msgpack/msgpack';
import { useDefaultServiceGetCandles } from 'gen/stream-api/queries';
import { CANDLE_IDX } from 'types/impliedVol';
import {
  useFetchParquetContourData,
  useFetchTwelveDataCandles,
  useGetEquitiesBySyms,
  useTraceStreaming,
  CANDLE_REFETCH_INTERVAL_MS,
} from 'hooks/trace/useTraceStreaming';

const END_CHART_X_PADDING = 50;

// how many days before today to transition to using the "twelve data" rather
// than the candles interface
const NUM_DAYS_IN_PAST_TO_USE_TWELVE_DATA = 6;

const SLIDER_INTERVAL_SECONDS = 10 * 60;

const CONTOUR_DATA_SETTLE_DELAY_MINUTES = 5;
const CONTOUR_PARQUET_DATA_REFETCH_TIME_MS = 30_000;

// the accumulation window of the candles fetched from the REST API as opposed
// to those of the streaming websocket interface
const CANDLE_REST_API_WINDOW_TIME_MS = 5_000;

const EQUITIES_BY_SYM_REFETCH_TIME_MS = 10 * 60_000;

const PRICE_LINE_SYMBOL = 'SPX';

type TraceProps = {
  productType: ProductType;
};

export const Trace = ({ productType }: TraceProps) => {
  const theme = useTheme();
  const { saveSgSettings } = useUserDetails();

  const { getParam, setParams } = useSetSym();

  const parquetKeys = useRecoilValue(oiIntradayParquetKeys);
  const invert = useRecoilValue(oiIntradayInvertedState);
  const priceBounds = useRecoilValue(oiIntradayPriceBoundsState);
  const tz = useRecoilValue(timezoneState);
  const screenWidth = useRecoilValue(screenWidthWithoutSidebarState);
  const screenHeight = useRecoilValue(screenHeightState);
  const isMobile = useRecoilValue(isMobileState);
  const strikeBarType = useRecoilValue(oiStrikeBarTypeState);
  const fullscreen = useRecoilValue(oiFullscreenState);
  const todaysOpenArr = useRecoilValue(todaysOpenArrState);
  const showKeyLevels = useRecoilValue(oiShowKeyLevelsState);
  const showGexZeroDte = useRecoilValue(oiShowGexZeroDteState);
  const showColorScale = useRecoilValue(oiShowColorScaleState);
  const showContourLines = useRecoilValue(oiShowContourLinesState);
  const oiPriceCandleDuration = useRecoilValue(oiPriceCandleDurationState);
  const selectedScaleRange = useRecoilValue(oiScaleRangeState);
  const hiroSymsMap = useRecoilValue(oiHiroSymsState);

  const [zoomData, setZoomData] = useState<ZoomData | undefined>();

  const [showChartType, setShowChartType] = useState(TraceShowChartType.Both);

  // if we have space, keep some x padding if possible
  const width = screenWidth * (isMobile ? 1 : 0.95);
  // TODO: make height calculation dynamic and not hardcoded
  let height = isMobile
    ? screenHeight - (fullscreen ? 220 : 350)
    : screenHeight * 0.95 -
      172 -
      (fullscreen ? 0 : 50) - // (top bar + title)
      (productType === ProductType.INTERNAL_OPEN_INTEREST ? 100 : 0); // extra row of settings
  height -= fullscreen ? 35 : 70; // support button

  useEffect(() => {
    // Mobile view allows the user to select between Heatmap or StrikeBars.
    // Normal view shows both, and doesn't allow the user to select either of those two.
    // Default to a heatmap when resizing down to mobile and restore to both when maximizing.
    setShowChartType(
      isMobile ? TraceShowChartType.Heatmap : TraceShowChartType.Both,
    );
  }, [isMobile]);

  const traceParams = useTraceParams({ productType });
  const {
    intradayDate,
    intradaySym,
    selectedLense,
    selectedGreek,
    heatmapColorSettings,
  } = traceParams;

  const selectedDateIsToday = intradayDate?.diff(dayjs(), 'days') < 1;

  const candleRefetchInterval = useMemo(() => {
    return selectedDateIsToday ? CANDLE_REFETCH_INTERVAL_MS : undefined;
  }, [selectedDateIsToday]);

  const contourDataRefetchInterval = useMemo(() => {
    return selectedDateIsToday
      ? CONTOUR_PARQUET_DATA_REFETCH_TIME_MS
      : undefined;
  }, [selectedDateIsToday]);

  // Plotly's heatmap takes raw `Date` objects in for the x-axis. In order to
  // display correctly, we need to shift the the raw timestamps by the offset
  // of the browser's timezone to the preferred timezone.
  const tzOffset = useMemo(() => getTzOffsetMs(tz), [tz]);

  const hiroSym = hiroSymsMap?.get(intradaySym) ?? intradaySym;

  const useTwelveData =
    intradayDate?.diff(dayjs(), 'days') < -NUM_DAYS_IN_PAST_TO_USE_TWELVE_DATA;

  // set the params initially so that the url is shareable from the get-go
  useEffect(
    () => {
      setParams({
        lense: selectedLense,
        date: getDateFormatted(intradayDate),
        traceSym: intradaySym,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { start, end } = expectedStartEndForSymbol(
    hiroSym || PRICE_LINE_SYMBOL,
    intradayDate,
    true,
    false,
    productType,
  );

  const {
    timestamp2StreamedDeltaCandle,
    timestamp2StreamedPrice,
    rerenderIndicator,
  } = useTraceStreaming({
    enabled: selectedDateIsToday,
    intradayDate,
    hiroSym,
    selectedLense,
  });

  const {
    data: contourDataRaw,
    isLoading: countourDataLoading,
    error: contourDataError,
  } = useFetchParquetContourData(
    {
      greek: selectedGreek,
      intradayDate,
      intradaySym,
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: contourDataRefetchInterval,
    },
  );

  const { data: gammaTable, error: gammaTableError } =
    useFetchParquetContourData(
      {
        greek: TraceLense.GAMMA,
        intradayDate,
        intradaySym,
      },
      undefined, // queryKey?: TQueryKey,
      {
        refetchInterval: contourDataRefetchInterval,
      },
    );

  const {
    data: candlesApiPriceArrayBuffer,
    isLoading: candlesApiPriceArrayBufferLoading,
    error: candlesApiError,
  } = useDefaultServiceGetCandles(
    {
      sym: PRICE_LINE_SYMBOL,
      start: start.valueOf(),
      end: end.valueOf(),
      field: 'price',
    },
    undefined, // queryKey?: TQueryKey,
    {
      enabled: !useTwelveData,
      refetchInterval: candleRefetchInterval,
      select: (v: ArrayBuffer) => v,
    },
  );

  const {
    data: twelveCandlesData,
    isLoading: twelveDataLoading,
    error: twelveDataError,
  } = useFetchTwelveDataCandles(
    { intradayDate, intradaySym: PRICE_LINE_SYMBOL, oiPriceCandleDuration },
    undefined, // queryKey?: TQueryKey,
    {
      enabled: useTwelveData,
      refetchInterval: candleRefetchInterval,
    },
  );

  const {
    data: deltaLineArrayBuffer,
    isLoading: deltaLineLoading,
    error: deltaLineError,
  } = useDefaultServiceGetCandles(
    {
      sym: hiroSym,
      start: start.valueOf(),
      end: end.valueOf(),
      option: 'total',
      filter: 'all',
      field: 'delta',
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: candleRefetchInterval,
    },
  );

  const deltaLineBucketed = useMemo(
    () => {
      if (!deltaLineArrayBuffer) {
        return;
      }
      const candles = decode(deltaLineArrayBuffer) as
        | Array<Array<number>>
        | undefined;
      if (!candles?.length) {
        return undefined;
      }
      let streamedCandles: Array<number[]> = [];
      if (selectedDateIsToday) {
        const lastCandleTimestamp =
          candles[candles.length - 1][CANDLE_IDX.TIMESTAMP_OPEN];
        const oldestStreamingCandleTimestamp =
          lastCandleTimestamp + CANDLE_REST_API_WINDOW_TIME_MS;
        streamedCandles = Array.from(timestamp2StreamedDeltaCandle.keys())
          .filter((ts: number): boolean => ts >= oldestStreamingCandleTimestamp)
          .sort((a, b) => a - b)
          .map((ts) => timestamp2StreamedDeltaCandle.get(ts));
      }

      let currentCandle = null;
      const result = [];
      for (const candleTuple of [...candles, ...streamedCandles]) {
        const bucketTime =
          Math.floor(
            candleTuple[CANDLE_IDX.TIMESTAMP_OPEN] /
              1_000 /
              oiPriceCandleDuration,
          ) *
          oiPriceCandleDuration *
          1_000;
        if (
          !currentCandle ||
          currentCandle[CANDLE_IDX.TIMESTAMP_OPEN] < bucketTime
        ) {
          currentCandle = candleTuple.slice();
          currentCandle[CANDLE_IDX.TIMESTAMP_OPEN] = bucketTime;
          result.push(currentCandle);
        } else {
          currentCandle[CANDLE_IDX.HIGH] = Math.max(
            candleTuple[CANDLE_IDX.HIGH],
            currentCandle[CANDLE_IDX.HIGH],
          );
          currentCandle[CANDLE_IDX.LOW] = Math.min(
            candleTuple[CANDLE_IDX.LOW],
            currentCandle[CANDLE_IDX.LOW],
          );
          currentCandle[CANDLE_IDX.CLOSE] += candleTuple[CANDLE_IDX.CLOSE];
        }
      }
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      deltaLineArrayBuffer,
      oiPriceCandleDuration,
      selectedDateIsToday,
      timestamp2StreamedDeltaCandle,
      rerenderIndicator,
    ],
  );

  const timestamps: Dayjs[] = useMemo<Dayjs[]>(() => {
    if (!contourDataRaw) {
      return [];
    }
    const seenTimes: Set<number> = new Set<number>();
    (contourDataRaw.toArray() as GammaHeatmapDatum[]).forEach((d) => {
      const bucketTime =
        Math.floor(+d.timestamp / 1_000 / SLIDER_INTERVAL_SECONDS) *
        SLIDER_INTERVAL_SECONDS *
        1_000;
      seenTimes.add(bucketTime);
    });

    const contourSettleTime = dayjs()
      .subtract(CONTOUR_DATA_SETTLE_DELAY_MINUTES, 'minutes')
      .valueOf();

    return Array.from(seenTimes.values())
      .sort((a, b) => a - b)
      .filter((t) => t <= contourSettleTime)
      .map((t) => dayjs(t));
  }, [contourDataRaw]);

  const timestampParam = useMemo(() => getParam('ts'), [getParam]);

  const setTimestamp = useCallback(
    (newVal?: dayjs.Dayjs | null) => {
      setParams({ ts: newVal?.format() });
    },
    [setParams],
  );

  const [timestamp, isLastTimestamp]: [Dayjs, boolean] = useMemo<
    [Dayjs, boolean]
  >(() => {
    if (timestampParam != null) {
      const ts = dayjs(timestampParam);

      if (
        ts.isValid() &&
        (timestamps.length === 0 || timestamps.find((time) => ts.isSame(time)))
      ) {
        const isLastTimestamp = ts.isSame(timestamps[timestamps.length - 1]);
        if (isLastTimestamp) {
          setTimestamp(undefined);
        }
        return [ts, isLastTimestamp];
      }
    }
    const result = timestamps[timestamps.length - 1];
    if (result) {
      const currentParamTs = dayjs(timestampParam);
      if (
        currentParamTs &&
        currentParamTs.isValid() &&
        result != currentParamTs &&
        timestampParam
      ) {
        setTimestamp(undefined);
      }
    }
    return [result, true];
  }, [timestamps, timestampParam, setTimestamp]);

  const isLatestTimestamp =
    timestamps.length > 0 &&
    timestamp != null &&
    timestamps[timestamps.length - 1] == timestamp;

  const { priceCandles, boundsStr } = useMemo<{
    priceCandles?: PriceCandleWithDateTime[];
    boundsStr?: string;
  }>(
    () => {
      let candles: PriceCandleWithDateTime[] | undefined = useTwelveData
        ? twelveCandlesData
        : candlesApiPriceArrayBuffer &&
          (decode(candlesApiPriceArrayBuffer) as Array<Array<number>>).map(
            (c) => {
              const [time, open, high, low, close] = splitCandle(c);
              return {
                time,
                datetime: dayjs(time),
                open,
                high,
                low,
                close,
              };
            },
          );
      if (!candles?.length) {
        return {};
      }
      let streamedCandles: PriceCandleWithDateTime[] = [];
      if (selectedDateIsToday) {
        const lastCandleTimestamp = candles[candles.length - 1].time;
        const oldestStreamingCandleTimestamp =
          lastCandleTimestamp + CANDLE_REST_API_WINDOW_TIME_MS;
        streamedCandles = Array.from(timestamp2StreamedPrice.keys())
          .filter((ts) => ts >= oldestStreamingCandleTimestamp)
          .sort((a, b) => a - b)
          .map((ts) => {
            const [_, open, high, low, close] = timestamp2StreamedPrice.get(
              ts,
            ) as number[];
            return {
              time: ts,
              datetime: dayjs(ts),
              open,
              high,
              low,
              close,
            };
          });
      }
      candles = [...candles, ...streamedCandles];

      const lo = candles.reduce((lo, c) => Math.min(lo, c.low), Infinity);
      const hi = candles.reduce((hi, c) => Math.max(hi, c.high), -Infinity);
      const boundsStr = `${lo * (1 - priceBounds)}, ${hi * (1 + priceBounds)}`;
      return { priceCandles: candles, boundsStr };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      candlesApiPriceArrayBuffer,
      twelveCandlesData,
      useTwelveData,
      priceBounds,
      timestamp2StreamedPrice,
      selectedDateIsToday,
      rerenderIndicator,
    ],
  );

  const getLastCandle = useCallback((): PriceCandleWithDateTime | undefined => {
    if (!priceCandles) {
      return;
    }
    let idx = priceCandles.length - 1;
    if (timestamp != null && !isLatestTimestamp) {
      const tgt = timestamp.valueOf();
      idx = predicateSearch(priceCandles, (c) => c.time <= tgt);
    }
    return priceCandles[idx];
  }, [priceCandles, timestamp, isLatestTimestamp]);

  const contourData = useMemo(() => {
    if (!contourDataRaw) {
      return;
    }
    const data = getContourData(
      contourDataRaw,
      timestamp?.valueOf(),
      parquetKeys,
      invert,
      boundsStr?.split(',')?.map((v: string): number => parseFloat(v)) ?? null,
      tzOffset,
      selectedLense,
      getLastCandle(),
      width,
    );

    const colorContourOptions = getContourColorOptions(
      data?.chartData?.z,
      heatmapColorSettings,
      selectedLense,
      selectedScaleRange,
    );
    return {
      ...data,
      chartData: {
        ...data?.chartData,
        ...colorContourOptions,
      },
    };
  }, [
    contourDataRaw,
    tzOffset,
    selectedLense,
    boundsStr,
    getLastCandle,
    heatmapColorSettings,
    invert,
    parquetKeys,
    selectedScaleRange,
    timestamp,
    width,
  ]);

  const minX = contourData?.firstChartTimestamp;
  const maxX = contourData?.lastChartTimestamp;

  const [minY, maxY]: [undefined | number, undefined | number] = useMemo<
    [undefined | number, undefined | number]
  >(() => {
    const contourStrikes = contourData?.chartData?.y ?? [];
    const min = contourStrikes[0];
    const max = contourStrikes[contourStrikes.length - 1];
    return [min, max];
  }, [contourData]);

  const deltaLine = useMemo(() => {
    if (!deltaLineBucketed || (deltaLineBucketed?.length ?? 0) <= 0 || !minX) {
      return undefined;
    }
    const color =
      heatmapColorSettings?.hiroColor ??
      theme?.palette?.trace?.heatmapSettings?.hiroColor;
    const maxTs = isLastTimestamp ? (maxX ?? 0) : timestamp?.valueOf();
    let deltaSumAccum = 0;
    const candles = deltaLineBucketed
      .map((candleTuple) => {
        deltaSumAccum += candleTuple[CANDLE_IDX.CLOSE];
        return {
          timestamp: candleTuple[CANDLE_IDX.TIMESTAMP_OPEN],
          closeAccum: deltaSumAccum,
        };
      })
      .filter((c) => c.timestamp >= minX && c.timestamp <= maxTs);
    const y = candles.map((c) => c.closeAccum);
    if (!y.length) {
      return undefined;
    }
    const min = y.reduce((accum, val) => Math.min(accum, val));
    const max = y.reduce((accum, val) => Math.max(accum, val));

    return {
      hoverinfo: 'skip',
      mode: 'lines',
      name: 'hiro',
      type: 'scatter',
      xaxis: 'x',
      yaxis: 'y2',
      candles,
      x: candles.map((c) =>
        dayjs(c.timestamp + tzOffset)
          .tz(tz)
          .toDate(),
      ),
      y,
      line: {
        width: 2,
        color,
      },
      min,
      max,
    };
  }, [
    deltaLineBucketed,
    heatmapColorSettings,
    maxX,
    minX,
    theme,
    tz,
    tzOffset,
    timestamp,
    isLastTimestamp,
  ]);

  const {
    strikeBarsData,
    strikeBarsTracker,
    strikeBarsRange,
    strikeBarsLoading,
    setStrikeBarsLoading,
    showZeroLine,
    triggerUpdate: triggerStrikeBarsUpdate,
  } = useStrikeBars({
    timestamp,
    timestamps,
    minY: minY ?? 0,
    maxY: maxY ?? 0,
    height,
    traceParams,
  });

  const { statsDataMap } = useStats({ intradaySym, intradayDate });

  const { data: equitiesBySyms } = useGetEquitiesBySyms(
    {
      endDate: intradayDate,
      sym: intradaySym,
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: EQUITIES_BY_SYM_REFETCH_TIME_MS,
    },
  );

  const priceCandlesBucketed = useMemo(() => {
    if (!priceCandles) {
      return;
    }
    let currentCandle = null;
    const result = [];
    for (const candle of priceCandles) {
      const bucketTime =
        Math.floor(candle.time / 1_000 / oiPriceCandleDuration) *
        oiPriceCandleDuration *
        1_000;
      if (!currentCandle || currentCandle.time < bucketTime) {
        currentCandle = { ...candle };
        currentCandle.time = bucketTime;
        result.push(currentCandle);
      } else {
        currentCandle.high = Math.max(candle.high, currentCandle.high);
        currentCandle.low = Math.min(candle.low, currentCandle.low);
        currentCandle.close = candle.close;
      }
    }
    return result;
  }, [oiPriceCandleDuration, priceCandles]);

  const priceCandleChartdata = useMemo(() => {
    if (!priceCandlesBucketed || !minX) {
      return;
    }
    const colors = safeMerge(
      theme?.palette?.trace?.heatmapSettings,
      heatmapColorSettings,
    );
    const maxTs = isLastTimestamp ? (maxX ?? 0) : timestamp?.valueOf();
    const candles = priceCandlesBucketed.filter(
      (c) => c.time >= minX && c.time <= maxTs,
    );

    return {
      name: 'price',
      type: 'candlestick',
      xaxis: 'x',
      hoverinfo: 'skip',
      candles,
      x: candles.map((c) =>
        dayjs(c.time + tzOffset)
          .tz(tz)
          .toDate(),
      ),
      open: candles.map((c) => c.open),
      high: candles.map((c) => c.high),
      low: candles.map((c) => c.low),
      close: candles.map((c) => c.close),
      decreasing: {
        fillcolor: colors?.candleDownFillColor,
        line: {
          color: colors?.candleDownLineColor,
        },
      },
      increasing: {
        fillcolor: colors?.candleUpFillColor,
        line: {
          color: colors?.candleUpLineColor,
        },
      },
    };
  }, [
    heatmapColorSettings,
    minX,
    maxX,
    theme,
    timestamp,
    tz,
    tzOffset,
    priceCandlesBucketed,
    isLastTimestamp,
  ]);

  const gammaAtLastPriceUninverted = useMemo<number | undefined>(() => {
    const lastCandle = getLastCandle();
    if (gammaTable == null || lastCandle == null) {
      return undefined;
    }

    const { y, z, rawChartTimes } = parseContourArray(
      gammaTable.toArray(),
      timestamp?.valueOf(),
      parquetKeys,
      false, // we want uninverted
      null, // we always want to look at all the data
      tzOffset,
      TraceLense.GAMMA,
    );

    return getGreekValueAtLastPrice(
      y,
      z,
      rawChartTimes,
      lastCandle,
      TraceLense.GAMMA,
    );
  }, [parquetKeys, timestamp, gammaTable, getLastCandle, tzOffset]);

  const levelsChartData = useMemo(() => {
    const contourX = contourData?.chartData?.x ?? [];
    if (
      equitiesBySyms == null ||
      contourData == null ||
      contourX.length === 0 ||
      !showKeyLevels ||
      showChartType === TraceShowChartType.StrikeBars
    ) {
      return { chartData: [], annotations: [] };
    }

    // get the min and max x coordinates for the chart since the lines we draw will need them so they appear
    // as horizontal lines
    const x1 = contourX[0];
    const x2 = contourX[contourX.length - 1];
    // always show the labels at 6 hours past the first coordinate
    // TODO: IMPROVE THIS IN FUTURE AND PLACE IT IN A BETTER LOCATION THAN 6 HOURS AHEAD
    const xMid = dayjs(x1).add(6, 'hour');

    // don't show levels that are not within the strike ranges
    const contourStrikes = contourData.chartData.y;
    const minY = (contourStrikes && contourStrikes[0]) ?? 0;
    const maxY =
      (contourStrikes && contourStrikes[contourStrikes.length - 1]) ?? 0;

    const priceLines = getPriceLines(
      equitiesBySyms as unknown as RawSpotgammaLevels,
      theme,
      sigHighLow(equitiesBySyms, todaysOpenArr, getDateFormatted(intradayDate)),
      true, // get key levels. `showKeyLevels` is guaranteed to be true here
    );
    const chartData: Plotly.Data[] = [];
    const annotations: Plotly.Data[] = [];

    Object.keys(priceLines).forEach((priceLineName) => {
      const levelData = priceLines[priceLineName as PriceLineKey];
      const val = levelData?.value ?? 0;
      if (val < minY || val > maxY) {
        return;
      }

      chartData.push({
        x: [x1, x2],
        y: [val, val],
        xaxis: 'x',
        yaxis: 'y',
        mode: 'lines',
        line: {
          color: levelData.color,
          width: 1,
        },
        name: priceLineName,
        hoverinfo: 'y',
      });

      // find the leftmost annotation with an overlapping y val
      let neighbor = null;
      for (const annotation of annotations) {
        if (Math.abs(annotation.y - val) <= 10) {
          if (neighbor == null || neighbor.x > annotation.x) {
            neighbor = annotation;
          }
        }
      }

      // subtract one hour from the left overlapping annotation label to ensure no overlap
      const xCoord =
        neighbor != null ? neighbor.x - 60 * 60 * 1000 : xMid.valueOf();

      annotations.push({
        x: xCoord,
        y: val,
        text: priceLineName,
        font: {
          size: 11,
          color: '#000000',
        },
        showarrow: false,
        bgcolor: hexToRGBA(levelData.color, 0.65),
        borderpad: 4,
      });
    });
    return { chartData, annotations };
  }, [
    contourData,
    equitiesBySyms,
    todaysOpenArr,
    theme,
    showKeyLevels,
    intradayDate,
    showChartType,
  ]);

  // strike bars are out of date if youre selecting the latest timestamp but the latest timestamp
  // we have for the strike bars is < than that
  const strikeBarsOutOfDate =
    timestamps.length > 0 &&
    selectedDateIsToday &&
    (timestamp?.valueOf() ?? 0) ===
      timestamps[timestamps.length - 1].valueOf() &&
    (strikeBarsData?.latestTimestamp ?? Infinity) <
      timestamps[timestamps.length - 1].valueOf();

  useEffect(
    () => {
      if (strikeBarsOutOfDate) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        triggerStrikeBarsUpdate();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // If:
      // * strikeBarsOutOfDate AND
      // * we fetch new strike bars and they too happen to be behind the latest timestamp
      // then, we will never refetch strike bars again without the following term.
      //
      // If the above race condition ever happens, we want to trigger another
      // fetch of strike bars on the next update of the timestamps.
      //
      // Hence, the following term:
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timestamps[timestamps.length - 1]?.valueOf(),
      strikeBarsOutOfDate,
    ],
  );

  const gexTitle = () => {
    if (isMobile) {
      return undefined;
    }

    const title = TRACE_BAR_TITLES.get(strikeBarType)!;
    if (
      showGexZeroDte &&
      (strikeBarsData?.chartData?.length ?? 0) > 0 &&
      strikeBarsData!.chartData[0].x.find((v: number) => v !== 0) == null
    ) {
      return `${title}<br />(market closed, 0DTE n/a)`;
    }

    if (strikeBarsOutOfDate) {
      return `${title}<br />(out of date, updating...)`;
    }

    return title;
  };

  const onZoom = useCallback(
    (eventData: Readonly<Plotly.PlotRelayoutEvent>) => {
      const xZoomedOut = eventData['xaxis.autorange'];
      const yZoomedOut = eventData['yaxis.autorange'];

      const xBounds = [
        eventData['xaxis.range[0]'],
        eventData['xaxis.range[1]'],
      ].filter((d) => d != null);
      const yBounds = [eventData['yaxis.range[0]'], eventData['yaxis.range[1]']]
        .filter((d) => d != null)
        .map((d) => Math.floor(d));

      const y2Bounds = [
        eventData['yaxis2.range[0]'] as number | undefined,
        eventData['yaxis2.range[1]'] as number | undefined,
      ]
        .filter((d) => d != null)
        .map((d) => Math.floor(d));

      const newZoomData: ZoomData = {};
      // for some reason, plotly sometimes does not pass in autorange for the y2 axis.
      // so we dont always know when y2 is zoomed out. instead, use the autorange for y
      if (!yZoomedOut) {
        newZoomData.y = yBounds.length > 0 ? yBounds : zoomData?.y;
        newZoomData.y2 = y2Bounds.length > 0 ? y2Bounds : zoomData?.y2;
      }
      if (!xZoomedOut) {
        newZoomData.x = xBounds.length > 0 ? xBounds : zoomData?.x;
      }
      const newZoomDataState =
        Object.keys(newZoomData).length > 0 ? newZoomData : undefined;
      setZoomData(newZoomDataState);
    },
    [zoomData],
  );

  const xAxisStartDomain = isMobile
    ? 0.15
    : strikeBarType === TraceStrikeBarType.NONE ||
        showChartType === TraceShowChartType.StrikeBars
      ? 0.05
      : 0.27;
  // ensure there is fixed padding at the end regardless of chart width
  const xAxisEndDomain = isMobile ? 1 : 1 - END_CHART_X_PADDING / width;
  const xAxis2Domain =
    showChartType === TraceShowChartType.StrikeBars
      ? [xAxisStartDomain, xAxisEndDomain]
      : [0, 0.19];

  const lastCandlesChartData = useMemo<{
    chartData?: Plotly.PlotData[];
    annotations?: Plotly.PlotData[];
  }>(() => {
    const contourX = contourData?.chartData?.x ?? [];
    const priceLastCandle = getLastCandle();
    const chartData: Plotly.PlotData[] = [];
    const annotations: Plotly.PlotData[] = [];

    if (contourX.length === 0) {
      return { chartData, annotations };
    }

    const hiroYArr = deltaLine?.y ?? [];
    const hiroLastClose = hiroYArr[hiroYArr.length - 1];

    const x1 = contourX[0];
    const x2 = contourX[contourX.length - 1];
    const opacity = 0.6;

    if (priceLastCandle != null) {
      const close = Math.round(priceLastCandle.close);
      const color = hexToRGBA(heatmapColorSettings.lastCandleColor, opacity);

      chartData.push({
        x: [x1, x2],
        y: [close, close],
        xaxis: 'x',
        yaxis: 'y',
        mode: 'lines',
        line: {
          dash: 'dot',
          color,
          width: 0.5,
        },
        name: close,
        hoverinfo: 'y',
      });

      annotations.push({
        x: xAxisStartDomain - 0.01,
        xanchor: 'right',
        xref: 'paper',
        y: close,
        text: `${close}`,
        font: {
          size: 11,
          color: theme.palette.background.default,
        },
        showarrow: false,
        bgcolor: theme.palette.text.primary,
        opacity,
        borderpad: 2,
      });
    }

    if (hiroLastClose != null) {
      const color = hexToRGBA(heatmapColorSettings.hiroColor, opacity);

      chartData.push({
        x: [x1, x2],
        y: [hiroLastClose, hiroLastClose],
        xaxis: 'x',
        yaxis: 'y2',
        mode: 'lines',
        line: {
          dash: 'dot',
          color,
          width: isMobile ? 2 : 0.5, // needs to be thicker to be visible on mobile
        },
        name: hiroLastClose,
        hoverinfo: 'y',
      });

      annotations.push({
        x: xAxisEndDomain + 0.01,
        xanchor: 'left',
        xref: 'paper',
        y: hiroLastClose,
        yaxis: 'y2',
        yref: 'y2',
        text: `${formatAsCompactNumber(hiroLastClose)}`,
        font: {
          size: 11,
          color: `#fff`,
        },
        showarrow: false,
        bgcolor: heatmapColorSettings.hiroColor,
        borderpad: 2,
      });
    }

    return { chartData, annotations };
  }, [
    contourData,
    isMobile,
    heatmapColorSettings,
    deltaLine,
    getLastCandle,
    theme,
    xAxisEndDomain,
    xAxisStartDomain,
  ]);

  const { onHover, hoverInfo, setHoverInfo, crosshairsChartData } = useTooltip({
    selectedLense,
    intradaySym,
    intradayDate,
    heatmapColorSettings,
    strikeBarsTracker,
    strikeBarsData,
    statsDataMap,
    deltaCandles: deltaLine?.candles,
    priceCandles: priceCandleChartdata?.candles,
    hiroSym,
    minY,
    maxY,
    minX,
    maxX,
  });

  const getPlotlyData = () => {
    const heatmapData: Plotly.PlotData[] = [
      priceCandleChartdata,
      contourData?.chartData
        ? {
            ...contourData?.chartData,
            showscale: showColorScale && !isMobile,
            line: {
              smoothing: 0.9,
              width: showContourLines ? undefined : 0,
              color: heatmapColorSettings.contourLineColor,
            },
          }
        : null,
      ...levelsChartData.chartData,
      deltaLine,
      ...(lastCandlesChartData.chartData ?? []),
      ...crosshairsChartData,
    ].filter((d) => d != null);

    let gexChartData: Plotly.PlotData[] = [];
    let trackerData: Plotly.PlotData[] = [];

    if (strikeBarType !== TraceStrikeBarType.NONE) {
      gexChartData =
        (strikeBarsData?.chartData?.length ?? 0) > 0 && !strikeBarsLoading
          ? strikeBarsData!.chartData
          : // needed for it to show loading even without data
            [{ xaxis: 'x2', type: 'bar', visible: false }];

      if (gexChartData && gexChartData[0].x && gexChartData[0].y) {
        trackerData = strikeBarsData?.trackerData ?? [];
      }
    }

    const strikeBarsDisplayData = [...trackerData, ...gexChartData].filter(
      (d) => d != null,
    );

    let chartData;
    let annotations = levelsChartData.annotations;

    if (showChartType === TraceShowChartType.Both) {
      chartData = heatmapData.concat(strikeBarsDisplayData);
      annotations = annotations.concat([
        ...lastCandlesChartData.annotations,
        ...(strikeBarsData?.annotations ?? []),
      ]);
    } else if (showChartType === TraceShowChartType.Heatmap) {
      chartData = heatmapData;
      annotations = annotations.concat(lastCandlesChartData.annotations);
    } else {
      chartData = strikeBarsDisplayData;
      annotations = strikeBarsData?.annotations ?? [];
    }

    return { chartData, annotations };
  };

  // if we have zoomData below, the array is just 2 items, the min and max
  const watermarkXArr = zoomData?.x
    ? zoomData.x.map((str) => dayjs(str))
    : contourData?.chartData?.x;
  const watermarkYArr = zoomData?.y ?? contourData?.chartData?.y;
  const watermarkInverseSize = isMobile ? 5 : 10; // lower value means bigger
  const watermark: Partial<Image> =
    showChartType === TraceShowChartType.StrikeBars ||
    (watermarkXArr?.length ?? 0) === 0 ||
    (watermarkYArr?.length ?? 0) === 0
      ? {}
      : {
          x: watermarkXArr![0].valueOf(),
          y: watermarkYArr![0],
          // same as above, these two formulas below are the product of repeated ui testing
          sizex:
            (watermarkXArr![watermarkXArr!.length - 1].valueOf() -
              watermarkXArr![0].valueOf()) /
            watermarkInverseSize,
          sizey:
            (watermarkYArr![watermarkYArr!.length - 1] - watermarkYArr![0]) /
            watermarkInverseSize,
          source: 'images/spotgamma-logo-full.png',
          xanchor: 'left',
          xref: 'x',
          yanchor: 'bottom',
          yref: 'y',
          layer: 'above',
          opacity: 0.5,
        };

  const plotlyData = getPlotlyData();

  const hasError =
    contourDataError ||
    gammaTableError ||
    candlesApiError ||
    twelveDataError ||
    deltaLineError;

  const loading =
    candlesApiPriceArrayBufferLoading ||
    twelveDataLoading ||
    deltaLineLoading ||
    countourDataLoading ||
    !contourData;

  const body = (
    <Loader
      isLoading={loading}
      message={
        hasError
          ? 'Error retrieving data. Automatically retrying...'
          : undefined
      }
    >
      <Center>
        <ExpandableContentWrapper
          isOpenState={oiFullscreenState}
          styleOverrides={{ background: theme.palette.background.default }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                backgroundColor: `${theme.palette.background.default} !important`,
                marginTop: isMobile ? 0 : '5px',
              }}
            >
              <Box>
                <TraceControls
                  timestamp={timestamp}
                  timestamps={timestamps}
                  productType={productType}
                  traceParams={traceParams}
                  zoomData={zoomData}
                  resetZoom={() => setZoomData(undefined)}
                  setStrikeBarType={(newType) => {
                    if (newType !== TraceStrikeBarType.NONE) {
                      if (
                        strikeBarParquetUrlForType(
                          newType,
                          intradayDate,
                          intradaySym,
                        ) !==
                        strikeBarParquetUrlForType(
                          strikeBarType,
                          intradayDate,
                          intradaySym,
                        )
                      ) {
                        // there is this very annoying lag where we set the label title first, then set the loading setting
                        // this is because of the slight lag caused by everything bubbling up into the useMemo/useEffects
                        // instead, setStrikeBarsLoading here if we anticipate needing to fetch
                        setStrikeBarsLoading(true);
                      }
                    }
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    saveSgSettings({ oi: { strikeBarType: newType } });
                  }}
                  showChartType={showChartType}
                  setShowChartType={setShowChartType}
                  statsData={statsDataMap.get(getDateFormatted(intradayDate))}
                  gammaAtLastPriceUninverted={gammaAtLastPriceUninverted}
                  lastUpdateCheckAt={dayjs()}
                />
              </Box>
              {isMobile && (
                <Box
                  margin="auto"
                  width={1}
                  textAlign="center"
                  marginBottom="10px"
                >
                  <ButtonGroup>
                    <Button
                      sx={{ fontSize: '13px', textTransform: 'none' }}
                      variant={
                        showChartType === TraceShowChartType.Heatmap
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        setShowChartType(TraceShowChartType.Heatmap)
                      }
                    >
                      Heatmap
                    </Button>
                    <Button
                      sx={{ fontSize: '13px', textTransform: 'none' }}
                      variant={
                        showChartType === TraceShowChartType.StrikeBars
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        setShowChartType(TraceShowChartType.StrikeBars)
                      }
                    >
                      Strike Bars
                    </Button>
                  </ButtonGroup>
                </Box>
              )}

              <Box margin="auto">
                <Plot
                  onRelayout={onZoom}
                  data={plotlyData.chartData}
                  onHover={onHover}
                  onUnhover={() => {
                    setHoverInfo(null);
                  }}
                  onClick={isMobile ? onHover : undefined}
                  config={{ showAxisDragHandles: false }}
                  layout={{
                    width,
                    height,
                    images: [watermark],
                    margin: {
                      l: 20,
                      r: isMobile ? 40 : 20,
                      b: isMobile ? 30 : 50,
                      t: 0,
                      pad: 4,
                    },
                    paper_bgcolor: theme.palette.background.default,
                    plot_bgcolor: theme.palette.background.default,
                    font: {
                      color: theme.palette.text.primary,
                    },
                    xaxis:
                      showChartType === TraceShowChartType.StrikeBars
                        ? {}
                        : {
                            rangeslider: {
                              visible: false,
                            },
                            domain: [xAxisStartDomain, xAxisEndDomain],
                            fixedrange: false,
                            range: zoomData?.x,
                          },
                    xaxis2:
                      strikeBarType === TraceStrikeBarType.NONE ||
                      showChartType === TraceShowChartType.Heatmap
                        ? {}
                        : {
                            domain: xAxis2Domain,
                            range: strikeBarsRange,
                            ...(strikeBarsLoading
                              ? {
                                  showline: false,
                                  zeroline: false,
                                  title: 'Updating. May take a minute...',
                                  showticklabels: false,
                                }
                              : {
                                  zeroline: showZeroLine,
                                  showline: false,
                                  title: {
                                    text: gexTitle(),
                                    font: {
                                      size: 12,
                                    },
                                  },
                                }),
                          },
                    yaxis: {
                      // you must specify a y range otherwise there will be unwanted padding with the
                      // tracker scatter trace on OI/NET_OI
                      range: getYRange(zoomData, minY, maxY, strikeBarType),
                      fixedrange: false,
                      ...(shouldShowAxisLabels(width)
                        ? {
                            title: {
                              text: 'Strike / Price ($)',
                              standoff: width > 1050 ? 15 : 5,
                            },
                          }
                        : {}),
                    },
                    yaxis2:
                      deltaLine == null
                        ? { fixedrange: false }
                        : {
                            fixedrange: false,
                            overlaying: 'y',
                            side: 'right',
                            position:
                              xAxisEndDomain + (isMobile ? -0.01 : 0.005),
                            range:
                              zoomData?.y2 ??
                              getHiroRange(deltaLine, priceBounds),
                            ...(shouldShowAxisLabels(width)
                              ? {
                                  title: {
                                    text: 'HIRO',
                                  },
                                }
                              : {}),
                          },
                    showlegend: false,
                    annotations: plotlyData.annotations,
                    barmode: 'overlay',
                    modebar: {
                      // remove all plotly buttons except the download as image one
                      // they simply dont work well with our chart
                      remove: [
                        'autoScale2d',
                        'hoverCompareCartesian',
                        'hovercompare',
                        'lasso2d',
                        'orbitRotation',
                        'pan2d',
                        'pan3d',
                        'resetCameraDefault3d',
                        'resetCameraLastSave3d',
                        'resetGeo',
                        'resetScale2d',
                        'resetViewMapbox',
                        'resetViews',
                        'select2d',
                        'sendDataToCloud',
                        'tableRotation',
                        'toggleHover',
                        'toggleSpikelines',
                        'togglehover',
                        'togglespikelines',
                        'zoom2d',
                        'zoom3d',
                        'zoomIn2d',
                        'zoomInGeo',
                        'zoomInMapbox',
                        'zoomOut2d',
                        'zoomOutGeo',
                        'zoomOutMapbox',
                        // remove toImage if mobile. if not, add dup key because ts complains
                        isMobile ? 'toImage' : 'zoom2d',
                      ],
                    },
                  }}
                />
              </Box>
              {hoverInfo}
              <Box>
                <TraceTimeSlider
                  timestamps={timestamps}
                  chartWidth={width}
                  timestamp={timestamp}
                  setTimestamp={setTimestamp}
                />
              </Box>
            </Box>
          </Box>
        </ExpandableContentWrapper>
      </Center>
    </Loader>
  );
  return (
    <Box sx={loading ? { width: 1, height: isMobile ? 1 : `${height}px` } : {}}>
      {body}
    </Box>
  );
};
